import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Select } from 'antd';
import type { ProcessDefinitionModalTabProps } from '../types';
import LLMModelConfig from './execution-logic/LLMModelConfig';
import UserDefinedFunctionConfig from './execution-logic/UserDefinedFunctionConfig';
import {
  EXECUTION_LOGIC_MODE_OPTIONS,
  type ExecutionLogicMode,
} from './execution-logic/shared';
import { useConfig } from '../../../../../hooks/useConfig';

const FieldBlock: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div>{children}</div>
    </div>
  );
};

const ExecutionLogicTab: React.FC<ProcessDefinitionModalTabProps> = ({
  ontologyId,
  processNodeId,
  definition,
  loading,
  onDefinitionUpdate,
  refreshTrigger,
}) => {
  const { globalConfig } = useConfig();
  const [mode, setMode] = useState<ExecutionLogicMode>('USER_DEFINED_FUNCTION');

  // 根据配置过滤过程类型选项
  const modeOptions = useMemo(() => {
    if (globalConfig.showLLMModelOption) return EXECUTION_LOGIC_MODE_OPTIONS;
    return EXECUTION_LOGIC_MODE_OPTIONS.filter(o => o.value !== 'LLM_MODEL');
  }, [globalConfig.showLLMModelOption]);

  useEffect(() => {
    if (definition?.type && /llm/i.test(definition.type)) {
      // 若 LLM 选项被隐藏，强制降级为用户定义函数
      setMode(
        globalConfig.showLLMModelOption ? 'LLM_MODEL' : 'USER_DEFINED_FUNCTION'
      );
      return;
    }

    setMode('USER_DEFINED_FUNCTION');
  }, [definition?.type, globalConfig.showLLMModelOption]);

  if (!processNodeId) {
    return (
      <div className="flex min-h-[320px] items-center justify-center py-6">
        <Empty description="暂无可配置的 Process 节点" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 py-2">
      <section className="flex flex-col gap-4">
        <div className="text-sm font-semibold text-gray-800">基本配置</div>
        <FieldBlock label="过程类型">
          <Select
            value={mode}
            onChange={value => setMode(value as ExecutionLogicMode)}
            options={modeOptions}
          />
        </FieldBlock>
      </section>

      {mode === 'USER_DEFINED_FUNCTION' ? (
        <UserDefinedFunctionConfig
          ontologyId={ontologyId}
          processNodeId={processNodeId}
          definition={definition}
          loading={loading}
          onDefinitionUpdate={onDefinitionUpdate}
          refreshTrigger={refreshTrigger}
        />
      ) : (
        <LLMModelConfig ontologyId={ontologyId} processNodeId={processNodeId} />
      )}
    </div>
  );
};

export default ExecutionLogicTab;
